import React, { useEffect, useState } from 'react';
import { httpsCallable } from '@/lib/callableProxy';
import { functions as firebaseFunctions } from '@/config/firebase';
import { toast as sonnerToast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Check, ArrowRight, Store } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSubscriptionPlans } from '@/hooks/useSubscriptionPlans';
import { Badge } from "@/components/ui/badge";

const SubscriptionPage: React.FC = () => {
  const { userSettings, isLoadingUserSettings, fetchUserSettings } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: plans = [], isLoading: isLoadingPlans } = useSubscriptionPlans();
  
  const [isAnnual, setIsAnnual] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  const activeWorkspace = userSettings?.activeWorkspace;
  const isActiveSubscriber =
    activeWorkspace?.stripeSubscriptionStatus === 'active' ||
    activeWorkspace?.stripeSubscriptionStatus === 'trialing' ||
    activeWorkspace?.stripeSubscriptionStatus === 'canceling';
  
  const proPlan = plans.find(p => p.planId === 'pro');

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('checkout_session_id')) {
      fetchUserSettings(); 
      navigate(location.pathname, { replace: true });
      sonnerToast.success("Subscription updated successfully!");
    }
  }, [location.search, fetchUserSettings, navigate]);

  const handleSubscribe = async () => {
    if (!proPlan || !userSettings?.activeWorkspaceId) return;
    setIsProcessing(true);
    
    const priceId = isAnnual ? proPlan.annualPriceId! : proPlan.priceId;

    try {
      const createCheckoutFn = httpsCallable(firebaseFunctions, 'createCheckoutSession');
      const result = await createCheckoutFn({
        priceId,
        workspaceId: userSettings.activeWorkspaceId,
        successUrl: `${window.location.origin}/payment-success?checkout_session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/subscriptions?checkout_cancelled=true`,
      });
      const data = result.data as any;
      if (data.url) window.location.href = data.url;
    } catch (e: any) {
      sonnerToast.error("Checkout failed. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManage = async () => {
    if (!userSettings?.activeWorkspaceId) return;
    setIsProcessing(true);
    try {
      const portalFn = httpsCallable(firebaseFunctions, 'createStripePortalSession');
      const result = await portalFn({ workspaceId: userSettings.activeWorkspaceId, returnUrl: window.location.href });
      const data = result.data as any;
      if (data.url) window.location.href = data.url;
    } catch (e) {
      sonnerToast.error("Failed to open billing portal.");
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoadingPlans || isLoadingUserSettings) return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin text-slate-400"/></div>;

  if (isActiveSubscriber) {
      return (
          <div className="max-w-3xl mx-auto py-20 px-6 text-center">
              <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-8">
                  <Store size={40} />
              </div>
              <h1 className="text-4xl font-serif font-medium text-slate-900 mb-4">You are on the Growth Plan.</h1>
              <p className="text-xl text-slate-500 mb-3">Enjoy lower platform fees and custom branding.</p>
              <p className="text-sm text-slate-500 mb-12">
                Manage renewals, invoices, and payment method here. Checkout financing/provider fees remain separate at transaction time.
              </p>
              <Button onClick={handleManage} variant="outline" size="lg" className="rounded-full px-8" disabled={isProcessing}>
                  {isProcessing ? <Loader2 className="animate-spin"/> : "Manage Subscription"}
              </Button>
          </div>
      );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] py-12 md:py-24 px-4">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
        
        <div className="space-y-10">
            <div>
                <Badge variant="secondary" className="mb-6 bg-teal-100 text-teal-800 hover:bg-teal-200 border-none px-4 py-1.5 text-sm font-medium rounded-full">
                    Upgrade to Growth
                </Badge>
                <h1 className="text-5xl md:text-6xl font-serif font-medium text-slate-900 leading-[1.1] mb-6">
                    Scale your studio <br/>
                    <span className="italic text-slate-500">with ease.</span>
                </h1>
                <p className="text-xl text-slate-500 leading-relaxed">
                    Reduce platform fees and unlock custom branding for your financing checkout pages.
                </p>
                <p className="mt-3 text-sm text-slate-500">
                    Plan pricing below is for LendLifted membership only. Payment processing and pay-over-time provider fees are charged separately through checkout.
                </p>
            </div>

            <div className="space-y-6">
                {[
                    { title: "Lower Platform Fees", desc: "Keep more of your earnings on every financed job." },
                    { title: "Custom Branding", desc: "Add your logo and colors to the customer checkout flow." },
                    { title: "Priority Support", desc: "Skip the line for technical assistance." },
                    { title: "Multiple Seats", desc: "Invite up to 5 team members to your workspace." },
                    { title: "Transparent Billing", desc: "Membership billing is separate from Stripe/Klarna/Affirm/Afterpay transaction costs." }
                ].map((feat, i) => (
                    <div key={i} className="flex gap-4">
                        <div className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0 mt-0.5">
                            <Check size={14} strokeWidth={3} />
                        </div>
                        <div>
                            <h3 className="font-bold text-slate-900 text-lg">{feat.title}</h3>
                            <p className="text-slate-500">{feat.desc}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>

        <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-tr from-slate-200 to-teal-100 rounded-[3rem] blur-2xl opacity-50 -z-10"></div>
            <Card className="border-none shadow-2xl bg-white rounded-[2.5rem] overflow-hidden">
                <CardContent className="p-10 md:p-12">
                    <div className="flex justify-center mb-8">
                         <div className="bg-slate-100 p-1.5 rounded-full flex relative">
                            <button 
                                onClick={() => setIsAnnual(false)}
                                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${!isAnnual ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
                            >
                                Monthly
                            </button>
                            <button 
                                onClick={() => setIsAnnual(true)}
                                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2 ${isAnnual ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:text-slate-900'}`}
                            >
                                Annual <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded text-white">Save 2 months</span>
                            </button>
                         </div>
                    </div>

                    <div className="text-center mb-10">
                        <div className="flex items-baseline justify-center gap-1">
                            <span className="text-6xl font-serif font-bold text-slate-900">${isAnnual ? '24' : '29'}</span>
                            <span className="text-xl text-slate-400">/mo</span>
                        </div>
                        <p className="text-sm text-slate-400 mt-2">
                            {isAnnual ? 'Billed $290 yearly' : 'Billed monthly'}
                        </p>
                    </div>

                    <Button 
                        size="lg" 
                        onClick={handleSubscribe} 
                        disabled={isProcessing}
                        className="w-full h-16 text-lg rounded-2xl bg-slate-900 hover:bg-slate-800 shadow-xl hover:scale-[1.02] transition-all"
                    >
                        {isProcessing ? <Loader2 className="animate-spin" /> : (
                            <span className="flex items-center gap-2">Upgrade Now <ArrowRight size={20} /></span>
                        )}
                    </Button>
                    <p className="text-center text-xs text-slate-400 mt-4">Cancel anytime. Membership charges are separate from transaction/provider fees.</p>
                </CardContent>
            </Card>
        </div>

      </div>
    </div>
  );
};

export default SubscriptionPage;
